import type { ModuleKey } from "../modules/registry";
import type { AccessRule } from "./access";
import type { Permission } from "./types";

export type PmsNavKey =
  | "live-board"
  | "reservations"
  | "groups"
  | "rooms"
  | "room-type-availability"
  | "room-availability-calendar"
  | "guests"
  | "stay"
  | "frontdesk"
  | "housekeeping"
  | "billing"
  | "night-audit"
  | "rates"
  | "channels"
  | "revenue"
  | "reports"
  | "interfaces"
  | "admin";

export type PmsActionKey =
  | "new-reservation"
  | "start-checkin"
  | "night-audit"
  | "walk-in"
  | "room-move"
  | "split-invoice"
  | "late-checkout"
  | "hk-task";

export type ModuleViewDefinition = {
  key: string;
  path: string;
  access: AccessRule;
};

export const SECTOR_PERMISSION_FAMILIES = {
  pms: [
    "pms.dashboard.view",
    "pms.incidents.update",
    "pms.reservations.view",
    "pms.reservations.create",
    "pms.reservations.edit",
    "pms.reservations.cancel",
    "pms.reservations.checkin",
    "pms.reservations.checkout",
    "pms.reservations.noshow",
    "pms.groups.view",
    "pms.rooms.view",
    "pms.rooms.assign",
    "pms.rooms.block",
    "pms.housekeeping.view",
    "pms.housekeeping.update",
    "pms.housekeeping.assign",
    "pms.guests.view",
    "pms.guests.create",
    "pms.guests.edit",
    "pms.guests.merge",
    "pms.billing.view",
    "pms.billing.post",
    "pms.billing.payment",
    "pms.billing.discount",
    "pms.billing.void",
    "pms.billing.comp",
    "pms.billing.transfer",
    "pms.billing.close",
    "pms.rates.view",
    "pms.rates.override",
    "pms.nightaudit.run",
    "pms.nightaudit.view",
    "pms.reports.view",
    "pms.reports.financial",
  ],
  ems: [
    "ems.assets.view",
    "ems.assets.edit",
    "ems.maintenance.view",
    "ems.maintenance.create",
    "ems.maintenance.edit",
    "ems.reports.view",
  ],
  wms: [
    "wms.inventory.view",
    "wms.inventory.adjust",
    "wms.orders.view",
    "wms.orders.create",
    "wms.orders.approve",
    "wms.orders.receive",
  ],
  revenue: [
    "revenue.analytics.view",
    "revenue.analytics.export",
    "revenue.pricing.view",
    "revenue.pricing.edit",
  ],
  voip: [
    "voip.calls.view",
    "voip.calls.manage",
    "voip.config.view",
    "voip.config.edit",
  ],
  guests: [
    "guests.profiles.view",
    "guests.profiles.edit",
    "guests.communication.view",
    "guests.communication.send",
    "guests.loyalty.view",
    "guests.loyalty.edit",
  ],
  cctv: [
    "cctv.live.view",
    "cctv.cameras.view",
    "cctv.cameras.manage",
    "cctv.recordings.view",
    "cctv.recordings.export",
    "cctv.events.view",
    "cctv.events.acknowledge",
    "cctv.incidents.view",
    "cctv.incidents.manage",
    "cctv.access_logs.view",
    "cctv.privacy.view",
    "cctv.privacy.manage",
    "cctv.health.view",
    "cctv.audit.view",
  ],
  keycoder: [
    "keycoder.cards.issue",
    "keycoder.cards.cancel",
    "keycoder.devices.view",
    "keycoder.devices.manage",
  ],
  garage: [
    "garage.parking.view",
    "garage.parking.manage",
    "garage.settings.view",
    "garage.settings.edit",
  ],
} as const satisfies Partial<Record<ModuleKey, readonly Permission[]>>;

// Module-level access — drives sidebar visibility and direct module entry.
// Authority is permission-derived: roles are metadata/assignment sets, while
// effective canonical permissions decide which HOS sectors can be opened.
export const MODULE_VISIBILITY: Partial<Record<ModuleKey, AccessRule>> = {
  identity: { anyPermissions: ["admin.access", "users.view", "users.manage", "users.create", "users.edit", "users.disable", "users.delete", "roles.manage", "identity.property.manage", "identity.property.assign"] },
  "identity-management": { anyPermissions: ["admin.access", "users.view", "users.manage", "users.create", "users.edit", "users.disable", "users.delete", "roles.manage", "identity.property.manage", "identity.property.assign"] },
  settings: { anyPermissions: ["admin.access", "settings.view", "settings.edit"] },
  pms: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.pms] },
  pos: { anyPermissions: ["pos.access"] },
  ems: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.ems] },
  voip: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.voip] },
  wms: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.wms] },
  revenue: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.revenue] },
  guests: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.guests] },
  cctv: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.cctv] },
  keycoder: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.keycoder] },
  garage: { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.garage] },
  "parking-preview": { anyPermissions: [...SECTOR_PERMISSION_FAMILIES.garage] },
  support: {
    anyPermissionPrefixes: ["pms.", "pos.", "ems.", "wms.", "voip.", "cctv.", "keycoder.", "garage."],
  },
  // Every signed-in user may send feedback about the app.
  feedback: {},
};

// PMS sub-navigation visibility.
export const PMS_NAV_VISIBILITY: Record<PmsNavKey, AccessRule> = {
  "live-board": {
    anyPermissions: ["pms.dashboard.view", "pms.reservations.view", "pms.rooms.view"],
  },
  reservations: { anyPermissions: ["pms.reservations.view", "pms.reservations.create", "pms.reservations.edit", "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout", "pms.reservations.noshow"] },
  groups: { anyPermissions: ["pms.groups.view"] },
  rooms: { anyPermissions: ["pms.rooms.view"] },
  "room-type-availability": { anyPermissions: ["pms.rooms.view", "pms.rooms.assign", "pms.rooms.block"] },
  "room-availability-calendar": { anyPermissions: ["pms.rooms.view", "pms.rooms.assign", "pms.rooms.block"] },
  guests: { anyPermissions: ["pms.guests.view", "pms.guests.create", "pms.guests.edit", "pms.guests.merge", "guests.profiles.view", "guests.profiles.edit", "guests.communication.view", "guests.communication.send", "guests.loyalty.view", "guests.loyalty.edit"] },
  stay: { anyPermissions: ["pms.reservations.view", "pms.reservations.edit", "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout", "pms.reservations.noshow"] },
  frontdesk: { anyPermissions: ["pms.dashboard.view", "pms.reservations.checkin", "pms.reservations.checkout", "pms.rooms.assign", "pms.incidents.update"] },
  housekeeping: { anyPermissions: ["pms.housekeeping.view", "pms.housekeeping.update", "pms.housekeeping.assign"] },
  billing: { anyPermissions: ["pms.billing.view", "pms.billing.post", "pms.billing.payment", "pms.billing.discount", "pms.billing.void", "pms.billing.comp", "pms.billing.transfer", "pms.billing.close"] },
  "night-audit": { anyPermissions: ["pms.nightaudit.view", "pms.nightaudit.run"] },
  rates: { anyPermissions: ["pms.rates.view", "pms.rates.override"] },
  // Channel manager (Channex): onboarding creates objects in a paid account
  // and, in production, lists real rooms on OTAs — administrators only.
  channels: { anyPermissions: ["admin.access"] },
  revenue: { anyPermissions: ["revenue.analytics.view", "revenue.analytics.export", "revenue.pricing.view", "revenue.pricing.edit"] },
  reports: { anyPermissions: ["pms.reports.view", "pms.reports.financial", "reports.view"] },
  interfaces: { anyPermissions: ["admin.access"] },
  admin: { anyPermissions: ["admin.access"] },
};

export const MODULE_VIEW_VISIBILITY: Partial<Record<ModuleKey, ModuleViewDefinition[]>> = {
  pms: [
    { key: "live-board", path: "/module/pms", access: PMS_NAV_VISIBILITY["live-board"] },
    { key: "reservations", path: "/module/pms/reservations", access: PMS_NAV_VISIBILITY.reservations },
    { key: "groups", path: "/module/pms/groups", access: PMS_NAV_VISIBILITY.groups },
    { key: "room-type-availability", path: "/module/pms/room-type-availability", access: PMS_NAV_VISIBILITY["room-type-availability"] },
    { key: "room-availability-calendar", path: "/module/pms/room-availability-calendar", access: PMS_NAV_VISIBILITY["room-availability-calendar"] },
    { key: "rooms", path: "/module/pms/rooms", access: PMS_NAV_VISIBILITY.rooms },
    { key: "guests", path: "/module/pms/guests", access: PMS_NAV_VISIBILITY.guests },
    { key: "stay", path: "/module/pms/stay", access: PMS_NAV_VISIBILITY.stay },
    { key: "frontdesk", path: "/module/pms/frontdesk", access: PMS_NAV_VISIBILITY.frontdesk },
    { key: "housekeeping", path: "/module/pms/housekeeping", access: PMS_NAV_VISIBILITY.housekeeping },
    { key: "billing", path: "/module/pms/billing", access: PMS_NAV_VISIBILITY.billing },
    { key: "night-audit", path: "/module/pms/night-audit", access: PMS_NAV_VISIBILITY["night-audit"] },
    { key: "rates", path: "/module/pms/rates", access: PMS_NAV_VISIBILITY.rates },
    { key: "channels", path: "/module/pms/channels", access: PMS_NAV_VISIBILITY.channels },
    { key: "revenue", path: "/module/pms/revenue", access: PMS_NAV_VISIBILITY.revenue },
    { key: "reports", path: "/module/pms/reports", access: PMS_NAV_VISIBILITY.reports },
    { key: "interfaces", path: "/module/pms/interfaces", access: PMS_NAV_VISIBILITY.interfaces },
  ],
  garage: [
    { key: "parking", path: "/module/garage", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "sessions", path: "/module/garage/sessions", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "billing", path: "/module/garage/billing", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "devices", path: "/module/garage/devices", access: { anyPermissions: ["garage.settings.view", "garage.settings.edit"] } },
    { key: "reports", path: "/module/garage/reports", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "settings", path: "/module/garage/settings", access: { anyPermissions: ["garage.settings.view", "garage.settings.edit"] } },
  ],
  cctv: [
    { key: "live", path: "/module/cctv", access: { anyPermissions: ["cctv.live.view"] } },
    { key: "cameras", path: "/module/cctv/cameras", access: { anyPermissions: ["cctv.cameras.view", "cctv.cameras.manage"] } },
    { key: "recordings", path: "/module/cctv/recordings", access: { anyPermissions: ["cctv.recordings.view", "cctv.recordings.export"] } },
    { key: "incidents", path: "/module/cctv/incidents", access: { anyPermissions: ["cctv.incidents.view", "cctv.incidents.manage"] } },
    { key: "events", path: "/module/cctv/events", access: { anyPermissions: ["cctv.events.view", "cctv.events.acknowledge"] } },
    { key: "access", path: "/module/cctv/access", access: { anyPermissions: ["cctv.access_logs.view"] } },
    { key: "privacy", path: "/module/cctv/privacy", access: { anyPermissions: ["cctv.privacy.view", "cctv.privacy.manage", "cctv.audit.view"] } },
    { key: "health", path: "/module/cctv/health", access: { anyPermissions: ["cctv.health.view"] } },
  ],
  ems: [
    { key: "live-board", path: "/module/ems", access: { anyPermissions: ["ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit", "ems.assets.view", "ems.assets.edit"] } },
    { key: "work-orders", path: "/module/ems/work-orders", access: { anyPermissions: ["ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit"] } },
    { key: "preventive", path: "/module/ems/preventive", access: { anyPermissions: ["ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit"] } },
    { key: "assets", path: "/module/ems/assets", access: { anyPermissions: ["ems.assets.view", "ems.assets.edit"] } },
    { key: "sla", path: "/module/ems/sla", access: { anyPermissions: ["ems.reports.view"] } },
    { key: "reports", path: "/module/ems/reports", access: { anyPermissions: ["ems.reports.view"] } },
    { key: "settings", path: "/module/ems/settings", access: { anyPermissions: ["ems.assets.edit"] } },
  ],
  wms: [
    { key: "dashboard", path: "/module/wms", access: { anyPermissions: ["wms.inventory.view", "wms.inventory.adjust", "wms.orders.view", "wms.orders.create", "wms.orders.approve", "wms.orders.receive"] } },
    { key: "overview", path: "/module/wms/overview", access: { anyPermissions: ["wms.inventory.view", "wms.inventory.adjust"] } },
    { key: "warnings", path: "/module/wms/warnings", access: { anyPermissions: ["wms.inventory.view", "wms.inventory.adjust"] } },
  ],
  voip: [
    { key: "dashboard", path: "/module/voip", access: { anyPermissions: ["voip.calls.view", "voip.calls.manage"] } },
    { key: "providers", path: "/module/voip/providers", access: { anyPermissions: ["voip.config.view", "voip.config.edit"] } },
    { key: "calls", path: "/module/voip/calls", access: { anyPermissions: ["voip.calls.view", "voip.calls.manage"] } },
    { key: "roles", path: "/module/voip/roles", access: { anyPermissions: ["voip.config.view", "voip.config.edit"] } },
    { key: "rooms", path: "/module/voip/rooms", access: { anyPermissions: ["voip.calls.view", "voip.calls.manage"] } },
    { key: "reporting", path: "/module/voip/reporting", access: { anyPermissions: ["voip.calls.view", "voip.calls.manage"] } },
    { key: "security", path: "/module/voip/security", access: { anyPermissions: ["voip.config.view", "voip.config.edit"] } },
    { key: "settings", path: "/module/voip/settings", access: { anyPermissions: ["voip.config.view", "voip.config.edit"] } },
  ],
  keycoder: [
    { key: "dashboard", path: "/module/keycoder", access: { anyPermissions: ["keycoder.cards.issue", "keycoder.cards.cancel", "keycoder.devices.view", "keycoder.devices.manage"] } },
    { key: "issuance", path: "/module/keycoder/issuance", access: { anyPermissions: ["keycoder.cards.issue"] } },
    { key: "active", path: "/module/keycoder/active", access: { anyPermissions: ["keycoder.cards.cancel", "keycoder.cards.issue"] } },
    { key: "devices", path: "/module/keycoder/devices", access: { anyPermissions: ["keycoder.devices.view", "keycoder.devices.manage"] } },
    { key: "logs", path: "/module/keycoder/logs", access: { anyPermissions: ["keycoder.cards.issue", "keycoder.cards.cancel", "keycoder.devices.view", "keycoder.devices.manage"] } },
    { key: "security", path: "/module/keycoder/security", access: { anyPermissions: ["keycoder.devices.manage"] } },
    { key: "integrations", path: "/module/keycoder/integrations", access: { anyPermissions: ["keycoder.devices.manage"] } },
  ],
  "parking-preview": [
    { key: "operations", path: "/module/parking-preview", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "map", path: "/module/parking-preview/map", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "arrivals", path: "/module/parking-preview/arrivals", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "history", path: "/module/parking-preview/history", access: { anyPermissions: ["garage.parking.view", "garage.parking.manage"] } },
    { key: "configuration", path: "/module/parking-preview/configuration", access: { anyPermissions: ["garage.settings.view", "garage.settings.edit"] } },
  ],
  identity: [
    { key: "users", path: "/module/identity", access: { anyPermissions: ["admin.access", "users.manage", "users.create", "users.edit", "users.disable", "users.delete", "roles.manage", "identity.property.manage", "identity.property.assign"] } },
  ],
  "identity-management": [
    { key: "directory", path: "/module/identity-management", access: { anyPermissions: ["users.view"] } },
  ],
  settings: [
    { key: "overview", path: "/settings/overview", access: { anyPermissions: ["admin.access", "settings.view", "settings.edit"] } },
  ],
};

// PMS action-level visibility (buttons, quick actions).
export const PMS_ACTION_VISIBILITY: Record<PmsActionKey, AccessRule> = {
  "new-reservation": { anyPermissions: ["pms.reservations.create"] },
  "start-checkin": { anyPermissions: ["pms.reservations.checkin"] },
  "night-audit": { anyPermissions: ["pms.nightaudit.run"] },
  "walk-in": { anyPermissions: ["pms.reservations.checkin", "pms.reservations.create"] },
  "room-move": { anyPermissions: ["pms.rooms.assign"] },
  "split-invoice": { anyPermissions: ["pms.billing.transfer"] },
  "late-checkout": { anyPermissions: ["pms.reservations.edit"] },
  "hk-task": { anyPermissions: ["pms.housekeeping.assign"] },
};
